import React, { useState } from 'react';
import { TextButton } from '../../styles/button.style';
import { useVerificationStore } from '../../context/VerificationContext';
import usePostUpdateVerificationData from '../../api/usePostUpdateVerificationData';
import { useToast } from '../../app/useToast';
import { canShowComponentForRole } from '../../utils/verification/verificationUtils';
import { useAuthStore } from '../../context/AuthContext';
import { ReprocessConfirmWrapper } from '../../pages/verification/Verification.styles';

const ReprocessReportButton: React.FC = () => {
    const { selectedReportMetadata } = useVerificationStore();
    const { reprocessReport } = usePostUpdateVerificationData();
    const { toastSuccess, toastError } = useToast();
    const { role } = useAuthStore();
    const [showConfirm, setShowConfirm] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const handleReprocessClick = () => {
        setShowConfirm(true);
    };

    const handleConfirm = async () => {
        if (!selectedReportMetadata?.reportId) {
            toastError('No report selected');
            return;
        }

        setIsProcessing(true);
        try {
            const result = await reprocessReport(selectedReportMetadata.reportId);
            if (result) {
                toastSuccess('Report marked for reprocessing. The report will be regenerated shortly.');
                setShowConfirm(false);
            } else {
                toastError('Failed to trigger report reprocessing');
            }
        } catch (error) {
            toastError('An error occurred while triggering reprocessing');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleCancel = () => {
        setShowConfirm(false);
    };

    if (!canShowComponentForRole(role)) {
        return null;
    }

    return (
        <>
            {selectedReportMetadata && (
                <div className="field-btn">
                    <TextButton className="inline" onClick={handleReprocessClick} disabled={isProcessing}>
                        Reprocess Report
                    </TextButton>

                    {showConfirm && (
                        <ReprocessConfirmWrapper>
                            <p>
                                This will mark the report for reprocessing by the system. A new version of the report will be created within
                                approximately an hour. Note that changes in AOL will automatically trigger report reprocessing, but if manual
                                reprocessing is needed (e.g. other corrections like UN LOCODE), it may take some time for changes to be propagated to
                                the data warehouse, so it is recommended to wait a while before reprocessing.
                            </p>
                            <div className="button-row">
                                <TextButton className="inline" onClick={handleConfirm} disabled={isProcessing}>
                                    {isProcessing ? 'Processing...' : 'Confirm'}
                                </TextButton>
                                <TextButton className="inline" onClick={handleCancel} disabled={isProcessing}>
                                    Cancel
                                </TextButton>
                            </div>
                        </ReprocessConfirmWrapper>
                    )}
                </div>
            )}
        </>
    );
};

export default ReprocessReportButton;
